import React, { useState, useEffect } from 'react';
import { Card, Input, Button, Space, Typography, Tooltip, Alert, message } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, KeyOutlined, SafetyOutlined, CopyOutlined, InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { ApiConfig } from '../../services/ApiConfigs';
import { successMsg, errorMsg } from '../../utils/ShowMessage';
import { ApiServices } from '../../services/apiServices';

const { Text, Title } = Typography;

/**
 * Helper function to sort top-level keys only (matching PHP ksort behavior)
 */
function sortObjectKeysTopLevel(obj) {
    if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) {
        return obj;
    }
    const sorted = {};
    Object.keys(obj).sort().forEach(key => {
        sorted[key] = obj[key];
    });
    return sorted;
}

/**
 * Generate HMAC SHA256 signature using Web Crypto API
 */
async function generateSignature(secretKey, requestBody, timestamp = null) {
    let payload;
    
    if (typeof requestBody === 'string') {
        payload = requestBody;
    } else if (requestBody && typeof requestBody === 'object') {
        const sortedBody = sortObjectKeysTopLevel(requestBody);
        payload = JSON.stringify(sortedBody);
    } else {
        payload = JSON.stringify(requestBody);
    }
    
    const dataToSign = timestamp ? `${timestamp}.${payload}` : payload;
    
    // Use Web Crypto API for HMAC
    const encoder = new TextEncoder();
    const keyData = encoder.encode(secretKey);
    const messageData = encoder.encode(dataToSign);
    
    // Import key for HMAC
    const cryptoKey = await crypto.subtle.importKey(
        'raw',
        keyData,
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign']
    );
    
    // Sign the data
    const signatureBuffer = await crypto.subtle.sign('HMAC', cryptoKey, messageData);
    
    // Convert to hex string
    const signatureArray = Array.from(new Uint8Array(signatureBuffer));
    const signatureHex = signatureArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    return signatureHex;
}

const SignatureTester = () => {
    const [apiKey, setApiKey] = useState('');
    const [secretKey, setSecretKey] = useState('');
    const [loading, setLoading] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [generatedSignature, setGeneratedSignature] = useState(null);
    const [timestamp, setTimestamp] = useState(null);
    const [copiedField, setCopiedField] = useState(null);

    // Load API keys from StoreApiKeys if available
    useEffect(() => {
        const loadApiKeys = async () => {
            try {
                const res = await ApiServices('get', ApiConfig.STORE_API_KEYS_MY_STORE);
                if (res.success && res.data) {
                    setApiKey(res.data.apiKey || '');
                    setSecretKey(res.data.secretKey || '');
                }
            } catch (error) {
                // Silently fail - user can enter manually
                console.log('Could not load API keys:', error);
            }
        };
        loadApiKeys();
    }, []);

    const copyToClipboard = async (text, fieldName) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(fieldName);
            successMsg(`${fieldName} copied to clipboard!`);
            setTimeout(() => setCopiedField(null), 2000);
        } catch (err) {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopiedField(fieldName);
                successMsg(`${fieldName} copied to clipboard!`);
                setTimeout(() => setCopiedField(null), 2000);
            } catch (fallbackErr) {
                errorMsg('Failed to copy to clipboard');
            }
            document.body.removeChild(textArea);
        }
    };

    const testSignature = async () => {
        try {
            setLoading(true);
            setTestResult(null);
            setGeneratedSignature(null);

            if (!apiKey || !secretKey) {
                errorMsg('Please enter both API Key and Secret Key');
                return;
            }

            // Fixed test body: {"help": "test"}
            const testBody = { help: 'test' };
            
            // Generate timestamp
            const currentTimestamp = Math.floor(Date.now() / 1000);
            setTimestamp(currentTimestamp);

            // Generate signature
            const signature = await generateSignature(secretKey, testBody, currentTimestamp);
            setGeneratedSignature(signature);

            // Make API request to test endpoint
            const response = await fetch(ApiConfig.TEST_SIGNATURE, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': apiKey,
                    'x-signature': signature,
                    'x-timestamp': currentTimestamp.toString()
                },
                body: JSON.stringify(testBody)
            });

            const result = await response.json();

            if (response.ok && result.success) {
                setTestResult({ valid: true, data: result });
                successMsg('Signature verification successful! ✅');
            } else {
                setTestResult({ valid: false, data: result });
                errorMsg(result.message || 'Signature verification failed');
            }
        } catch (error) {
            console.error('Error testing signature:', error);
            errorMsg(error.message || 'Failed to test signature');
            setTestResult({ valid: false, error: error.message });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card
            title={
                <Space>
                    <div style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <SafetyOutlined style={{ fontSize: 16, color: '#ffffff' }} />
                    </div>
                    <span style={{ fontWeight: 600 }}>API Key & Signature Test</span>
                </Space>
            }
            style={{ 
                height: '100%',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
            }}
        >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                        API Key <Text type="secondary">(Public Key)</Text>
                    </Text>
                    <Input
                        placeholder="mc_pub_abc123xyz456def789ghi012jkl"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        prefix={<KeyOutlined />}
                        size="large"
                    />
                </div>

                <div>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                        Secret Key <Text type="secondary">(Private Key - Never share this!)</Text>
                    </Text>
                    <Input.Password
                        placeholder="mc_sk_xyz789abc123def456ghi012jkl34"
                        value={secretKey}
                        onChange={(e) => setSecretKey(e.target.value)}
                        prefix={<KeyOutlined />}
                        size="large"
                    />
                </div>

                <Alert
                    message="Test Body"
                    description={
                        <div>
                            <Text code style={{ fontSize: 12 }}>{"{ \"help\": \"test\" }"}</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                This endpoint always uses the fixed test body above.
                            </Text>
                        </div>
                    }
                    type="info"
                    showIcon
                    icon={<InfoCircleOutlined />}
                    style={{ marginBottom: 8 }}
                />

                <Button 
                    type="primary" 
                    onClick={testSignature} 
                    loading={loading} 
                    icon={<SafetyOutlined />}
                    size="large"
                    block
                >
                    Test Signature Verification
                </Button>

                {generatedSignature && timestamp && (
                    <div style={{ 
                        marginTop: 16, 
                        padding: 16, 
                        backgroundColor: '#f5f5f5', 
                        borderRadius: 8,
                        border: '1px solid #e8e8e8'
                    }}>
                        <Title level={5} style={{ marginBottom: 12 }}>Generated Signature Details</Title>
                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                            <div>
                                <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
                                    Signature:
                                </Text>
                                <Input.Group compact style={{ display: 'flex' }}>
                                    <Input
                                        value={generatedSignature}
                                        readOnly
                                        style={{ flex: 1, fontSize: 11, fontFamily: 'monospace' }}
                                    />
                                    <Tooltip title={copiedField === 'signature' ? 'Copied!' : 'Copy'}>
                                        <Button
                                            icon={<CopyOutlined />}
                                            onClick={() => copyToClipboard(generatedSignature, 'signature')}
                                            type={copiedField === 'signature' ? 'primary' : 'default'}
                                        >
                                            {copiedField === 'signature' ? 'Copied' : 'Copy'}
                                        </Button>
                                    </Tooltip>
                                </Input.Group>
                            </div>

                            <div>
                                <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
                                    Timestamp:
                                </Text>
                                <Input.Group compact style={{ display: 'flex' }}>
                                    <Input
                                        value={timestamp}
                                        readOnly
                                        style={{ flex: 1, fontSize: 11, fontFamily: 'monospace' }}
                                    />
                                    <Tooltip title={copiedField === 'timestamp' ? 'Copied!' : 'Copy'}>
                                        <Button
                                            icon={<CopyOutlined />}
                                            onClick={() => copyToClipboard(timestamp.toString(), 'timestamp')}
                                            type={copiedField === 'timestamp' ? 'primary' : 'default'}
                                        >
                                            {copiedField === 'timestamp' ? 'Copied' : 'Copy'}
                                        </Button>
                                    </Tooltip>
                                </Input.Group>
                            </div>

                            <div style={{ marginTop: 8 }}>
                                <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                                    Request Headers:
                                </Text>
                                <div style={{ 
                                    padding: 12, 
                                    backgroundColor: '#fff', 
                                    borderRadius: 4, 
                                    fontSize: 11, 
                                    fontFamily: 'monospace',
                                    border: '1px solid #e8e8e8'
                                }}>
                                    <div style={{ marginBottom: 4 }}>
                                        <Text type="secondary">x-api-key:</Text> {apiKey.substring(0, 20)}...
                                    </div>
                                    <div style={{ marginBottom: 4 }}>
                                        <Text type="secondary">x-signature:</Text> {generatedSignature.substring(0, 30)}...
                                    </div>
                                    <div>
                                        <Text type="secondary">x-timestamp:</Text> {timestamp}
                                    </div>
                                </div>
                            </div>
                        </Space>
                    </div>
                )}

                {testResult && (
                    <div style={{
                        marginTop: 16,
                        padding: 16,
                        backgroundColor: testResult.valid ? '#f6ffed' : '#fff2f0',
                        border: `1px solid ${testResult.valid ? '#b7eb8f' : '#ffccc7'}`,
                        borderRadius: 8
                    }}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Space>
                                {testResult.valid ? (
                                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                                ) : (
                                    <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />
                                )}
                                <Text strong style={{ color: testResult.valid ? '#52c41a' : '#ff4d4f', fontSize: 16 }}>
                                    {testResult.valid ? 'Signature Verification Successful ✅' : 'Signature Verification Failed ❌'}
                                </Text>
                            </Space>
                            
                            {testResult.data?.message && (
                                <Text type="secondary" style={{ display: 'block', fontSize: 13 }}>
                                    {testResult.data.message}
                                </Text>
                            )}

                            {testResult.data?.data && (
                                <div style={{ marginTop: 8 }}>
                                    <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
                                        Store Information:
                                    </Text>
                                    <div style={{ fontSize: 12, color: '#595959' }}>
                                        {testResult.data.data.storeName && (
                                            <div>Store: {testResult.data.data.storeName}</div>
                                        )}
                                        {testResult.data.data.storeId && (
                                            <div>Store ID: {testResult.data.data.storeId}</div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {testResult.data?.debug && (
                                <div style={{ marginTop: 8, padding: 8, backgroundColor: '#fff', borderRadius: 4 }}>
                                    <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 11 }}>
                                        Debug Information:
                                    </Text>
                                    <pre style={{ fontSize: 10, margin: 0, overflow: 'auto' }}>
                                        {JSON.stringify(testResult.data.debug, null, 2)}
                                    </pre>
                                </div>
                            )}
                        </Space>
                    </div>
                )}

                <Alert
                    message="About This Test"
                    description={
                        <div>
                            <Text style={{ fontSize: 12 }}>
                                This endpoint tests your API key and signature verification. The request body is always 
                                <Text code>{"{ \"help\": \"test\" }"}</Text>. 
                                If the test succeeds, your API key and secret key are configured correctly and you can use them 
                                to create orders via the API.
                            </Text>
                        </div>
                    }
                    type="info"
                    showIcon
                    icon={<InfoCircleOutlined />}
                    style={{ marginTop: 8 }}
                />
            </Space>
        </Card>
    );
};

export default SignatureTester;
